import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { StaffVisit, VisitInput } from '@/lib/visitForm';

// Staff correcting a participant's centre visits. Every change also changes
// their percurso, the reports and, for a visit today, the front desk.

const visitsKey = (participantId: number) => ['participant', participantId, 'visits'] as const;

interface VisitsResponse {
  visits: StaffVisit[];
  total: number;
}

/** The participant's visits, newest first. */
export function useParticipantVisits(participantId: number | null) {
  return useQuery({
    queryKey: visitsKey(participantId ?? 0),
    queryFn: () => api.get<VisitsResponse>(`/api/participants/${participantId}/visits`),
    enabled: participantId !== null,
    staleTime: 1000 * 30,
  });
}

function useInvalidateVisits(participantId: number) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: visitsKey(participantId) });
    queryClient.invalidateQueries({ queryKey: ['participant', participantId, 'percurso'] });
    queryClient.invalidateQueries({ queryKey: ['presences'] });
    queryClient.invalidateQueries({ queryKey: ['reports'] });
  };
}

/** Record a visit nobody checked in at the time. */
export function useCreateVisit(participantId: number) {
  const invalidate = useInvalidateVisits(participantId);
  return useMutation({
    mutationFn: (data: VisitInput) =>
      api.post<StaffVisit>(`/api/participants/${participantId}/visits`, data),
    onSuccess: invalidate,
  });
}

/** Correct a visit's centre or times. */
export function useUpdateVisit(participantId: number) {
  const invalidate = useInvalidateVisits(participantId);
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: VisitInput }) =>
      api.put<StaffVisit>(`/api/centro-visits/${id}`, data),
    onSuccess: invalidate,
  });
}

/** Remove a visit that did not happen. */
export function useDeleteVisit(participantId: number) {
  const invalidate = useInvalidateVisits(participantId);
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/centro-visits/${id}`),
    onSuccess: invalidate,
  });
}
